import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { Locate, FileDown, ChevronRight, Loader2 } from 'lucide-react';
import Header from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import { api } from '../api/client.js';

// Defensive default-icon fix (we use a divIcon below, but other plugins might pull defaults).
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const TEAL = '#2EB6A7';
const RED_PRIORITY = '#c0392b';
const VIOLET_ANOMALY = '#7c3aed';
const JAUNE_POUBELLE = '#eab308';
const ORANGE_CENDRIER = '#f97316';
const ROUGE_ZS = '#ef4444';
const FRANCE_CENTER = [46.5, 2.5];
const FRANCE_ZOOM = 6;

// Custom teal dot icon (standard).
const tealDotIcon = L.divIcon({
  className: 'vrt-marker',
  html: `<span class="vrt-marker__dot" aria-hidden="true"></span>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
  popupAnchor: [0, -10],
});

// Custom red dot icon (priority).
const priorityRedIcon = L.divIcon({
  className: 'vrt-marker vrt-marker--priority',
  html: `<span class="vrt-marker__dot vrt-marker__dot--priority" aria-hidden="true"></span>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
  popupAnchor: [0, -10],
});

// Violet dot icon (anomaly).
const anomalyVioletIcon = L.divIcon({
  className: 'vrt-marker vrt-marker--anomaly',
  html: `<span class="vrt-marker__dot vrt-marker__dot--anomaly" aria-hidden="true"></span>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
  popupAnchor: [0, -10],
});

// SVG paths (lucide icons) — embarqués pour les divIcons Leaflet
const SVG_TRASH = '<path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/>';
const SVG_FLAME = '<path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>';
const SVG_ALERT = '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" x2="12" y1="9" y2="13"/><line x1="12" x2="12.01" y1="17" y2="17"/>';

function quickPointIcon(color, svg) {
  return L.divIcon({
    className: 'vrt-qmarker',
    html: `<span class="vrt-qmarker__bubble" style="background:${color}"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">${svg}</svg></span>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -12],
  });
}

const poubelleIcon = quickPointIcon(JAUNE_POUBELLE, SVG_TRASH);
const cendrierIcon = quickPointIcon(ORANGE_CENDRIER, SVG_FLAME);
const zoneSensibleIcon = quickPointIcon(ROUGE_ZS, SVG_ALERT);

// Persistance des filtres carte dans localStorage
const FILTERS_KEY = 'vrt.map.filters.v1';
function loadFilters() {
  try {
    const raw = localStorage.getItem(FILTERS_KEY);
    if (!raw) return { avaloirs: true, poubelles: true, cendriers: true, zones: true };
    const obj = JSON.parse(raw);
    return {
      avaloirs:  obj.avaloirs  !== false,
      poubelles: obj.poubelles !== false,
      cendriers: obj.cendriers !== false,
      zones:     obj.zones     !== false,
    };
  } catch {
    return { avaloirs: true, poubelles: true, cendriers: true, zones: true };
  }
}
function saveFilters(f) {
  try { localStorage.setItem(FILTERS_KEY, JSON.stringify(f)); } catch {}
}

function FitToBounds({ items }) {
  const map = useMap();
  const didFit = useRef(false);
  useEffect(() => {
    if (didFit.current) return;
    if (!items || items.length === 0) return;
    const valid = items.filter((a) => Number.isFinite(a.latitude) && Number.isFinite(a.longitude));
    if (valid.length === 0) return;
    const bounds = L.latLngBounds(valid.map((a) => [a.latitude, a.longitude]));
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 18 });
      didFit.current = true;
    }
  }, [items, map]);
  return null;
}

function LocateButton({ mapRef }) {
  const [busy, setBusy] = useState(false);
  const onClick = () => {
    const map = mapRef && mapRef.current;
    if (!navigator.geolocation || !map) return;
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        map.setView([pos.coords.latitude, pos.coords.longitude], 19);
        setBusy(false);
      },
      () => { setBusy(false); alert('Impossible de récupérer votre position.'); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="vrt-map__locate"
      disabled={busy}
      aria-label="Centrer sur ma position"
      title="Centrer sur ma position"
    >
      <Locate size={20} />
    </button>
  );
}

function truncate(str, n) {
  if (!str) return '';
  return str.length > n ? str.slice(0, n) + '…' : str;
}

export default function MapView() {
  const [items, setItems] = useState([]);
  const [quickPoints, setQuickPoints] = useState([]);
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [priorityOnly, setPriorityOnly] = useState(false);
  const [anomalyOnly, setAnomalyOnly] = useState(false);
  const [filters, setFilters] = useState(loadFilters);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [toast, setToast] = useState(null); // { msg, kind: 'success' | 'error' | 'info' }
  const toastTimer = useRef(null);
  const mapRef = useRef(null);

  const toggleFilter = (key) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      saveFilters(next);
      return next;
    });
  };

  useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
  }, []);

  const showToast = (msg, kind = 'info', ms = 3000) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ msg, kind });
    toastTimer.current = setTimeout(() => setToast(null), ms);
  };

  const onExportPdf = async () => {
    if (pdfExporting) return;
    setPdfExporting(true);
    showToast('Préparation du PDF en cours (jusqu’à 1 min)…', 'info', 60000);
    try {
      await api.download('/api/exports/pdf', 'vertuoso-avaloirs.pdf');
      showToast('PDF téléchargé', 'success', 2500);
    } catch (e) {
      showToast(`Export PDF : ${e.message || 'erreur'}`, 'error', 5000);
    } finally {
      setPdfExporting(false);
    }
  };

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [avaloirData, schemaData, quickData] = await Promise.all([
          api.get('/api/avaloirs?limit=500'),
          api.get('/api/survey/schema'),
          // Best-effort : si l'endpoint quick-points renvoie 404 (vieux back), on ignore
          api.get('/api/quick-points?limit=1000').catch(() => ({ items: [] })),
        ]);
        if (cancel) return;
        setItems(Array.isArray(avaloirData?.items) ? avaloirData.items : []);
        setSchema(schemaData);
        setQuickPoints(Array.isArray(quickData?.items) ? quickData.items : []);
      } catch (e) {
        if (!cancel) setError(e.message || 'Erreur de chargement');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const validItems = useMemo(
    () => {
      let filtered = items.filter((a) => Number.isFinite(a.latitude) && Number.isFinite(a.longitude));
      if (priorityOnly) {
        filtered = filtered.filter((a) => a.priority_life === true);
      }
      if (anomalyOnly) {
        filtered = filtered.filter((a) => a.anomaly === true);
      }
      return filtered;
    },
    [items, priorityOnly, anomalyOnly]
  );

  const validPoubelles = useMemo(
    () => quickPoints.filter((p) => p.type === 'poubelle' && Number.isFinite(p.latitude) && Number.isFinite(p.longitude)),
    [quickPoints]
  );

  const validCendriers = useMemo(
    () => quickPoints.filter((p) => p.type === 'cendrier' && Number.isFinite(p.latitude) && Number.isFinite(p.longitude)),
    [quickPoints]
  );

  const validZones = useMemo(
    () => quickPoints.filter((p) => p.type === 'zone_sensible' && Number.isFinite(p.latitude) && Number.isFinite(p.longitude)),
    [quickPoints]
  );

  // Items utilisés pour FitToBounds : tout ce qui est affiché selon les filtres
  const fitItems = useMemo(() => {
    const arr = [];
    if (filters.avaloirs)  arr.push(...validItems);
    if (filters.poubelles) arr.push(...validPoubelles);
    if (filters.cendriers) arr.push(...validCendriers);
    if (filters.zones)     arr.push(...validZones);
    return arr;
  }, [filters, validItems, validPoubelles, validCendriers, validZones]);

  return (
    <div className="vrt-map">
      <Header />

      <div className="vrt-map__wrap">
        {error && (
          <div className="vrt-map__error" role="alert">
            Impossible de charger les avaloirs : {error}
          </div>
        )}

        <MapContainer
          center={FRANCE_CENTER}
          zoom={FRANCE_ZOOM}
          minZoom={3}
          maxZoom={22}
          scrollWheelZoom
          style={{ width: '100%', height: '100%' }}
          ref={mapRef}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            maxNativeZoom={19}
            maxZoom={22}
          />

          <FitToBounds items={fitItems} />

          {filters.avaloirs && (
            <MarkerClusterGroup chunkedLoading disableClusteringAtZoom={17} maxClusterRadius={50}>
              {validItems.map((a) => (
                <Marker
                  key={`avaloir-${a.id}`}
                  position={[a.latitude, a.longitude]}
                  icon={
                    a.priority_life === true
                      ? priorityRedIcon
                      : a.anomaly === true
                        ? anomalyVioletIcon
                        : tealDotIcon
                  }
                >
                  <Popup>
                    <PopupContent a={a} />
                  </Popup>
                </Marker>
              ))}
            </MarkerClusterGroup>
          )}

          {filters.poubelles && validPoubelles.map((p) => (
            <Marker
              key={`poubelle-${p.id}`}
              position={[p.latitude, p.longitude]}
              icon={poubelleIcon}
            >
              <Popup>
                <QuickPointPopup p={p} />
              </Popup>
            </Marker>
          ))}

          {filters.cendriers && validCendriers.map((p) => (
            <Marker
              key={`cendrier-${p.id}`}
              position={[p.latitude, p.longitude]}
              icon={cendrierIcon}
            >
              <Popup>
                <QuickPointPopup p={p} />
              </Popup>
            </Marker>
          ))}

          {filters.zones && validZones.map((p) => (
            <Marker
              key={`zs-${p.id}`}
              position={[p.latitude, p.longitude]}
              icon={zoneSensibleIcon}
            >
              <Popup>
                <QuickPointPopup p={p} />
              </Popup>
            </Marker>
          ))}
        </MapContainer>

        {/* Panneau filtre + légende combiné, top-right */}
        <div className="vrt-map__filters" role="group" aria-label="Filtres carte">
          <label className="vrt-map__filter-row">
            <input
              type="checkbox"
              checked={filters.avaloirs}
              onChange={() => toggleFilter('avaloirs')}
            />
            <span className="vrt-map__filter-dot" style={{ background: TEAL }} />
            <span>Avaloirs <span className="vrt-map__filter-count">({validItems.length})</span></span>
          </label>
          <label className="vrt-map__filter-row">
            <input
              type="checkbox"
              checked={filters.poubelles}
              onChange={() => toggleFilter('poubelles')}
            />
            <span className="vrt-map__filter-dot" style={{ background: JAUNE_POUBELLE }} />
            <span>Poubelles <span className="vrt-map__filter-count">({validPoubelles.length})</span></span>
          </label>
          <label className="vrt-map__filter-row">
            <input
              type="checkbox"
              checked={filters.cendriers}
              onChange={() => toggleFilter('cendriers')}
            />
            <span className="vrt-map__filter-dot" style={{ background: ORANGE_CENDRIER }} />
            <span>Cendriers <span className="vrt-map__filter-count">({validCendriers.length})</span></span>
          </label>
          <label className="vrt-map__filter-row">
            <input
              type="checkbox"
              checked={filters.zones}
              onChange={() => toggleFilter('zones')}
            />
            <span className="vrt-map__filter-dot" style={{ background: ROUGE_ZS }} />
            <span>Zones sensibles <span className="vrt-map__filter-count">({validZones.length})</span></span>
          </label>
          <div className="vrt-map__filter-sep" />
          <label className="vrt-map__filter-row vrt-map__filter-row--strong">
            <input
              type="checkbox"
              checked={anomalyOnly}
              onChange={(e) => setAnomalyOnly(e.target.checked)}
              disabled={!filters.avaloirs}
            />
            <span className="vrt-map__filter-dot" style={{ background: VIOLET_ANOMALY }} />
            <span>Anomalies uniquement</span>
          </label>
          <label className="vrt-map__filter-row vrt-map__filter-row--strong">
            <input
              type="checkbox"
              checked={priorityOnly}
              onChange={(e) => setPriorityOnly(e.target.checked)}
              disabled={!filters.avaloirs}
            />
            <span className="vrt-map__filter-dot" style={{ background: RED_PRIORITY }} />
            <span>Priorité Life uniquement</span>
          </label>
        </div>

        <div className="vrt-map__floats vrt-map__floats--bl">
          <button
            type="button"
            onClick={onExportPdf}
            disabled={pdfExporting}
            className="vrt-map__btn vrt-map__btn--navy"
            aria-label={pdfExporting ? 'Génération du PDF en cours' : 'Exporter en PDF'}
            title={pdfExporting ? 'Génération en cours…' : 'Exporter en PDF'}
            style={{ border: 'none', cursor: pdfExporting ? 'progress' : 'pointer', opacity: pdfExporting ? 0.8 : 1 }}
          >
            {pdfExporting ? <Loader2 size={18} className="vrt-spin" /> : <FileDown size={18} />}
            <span>{pdfExporting ? 'Génération…' : 'Exporter PDF'}</span>
          </button>
        </div>

        <div className="vrt-map__floats vrt-map__floats--br">
          <LocateButton mapRef={mapRef} />
        </div>

        {loading && (
          <div className="vrt-map__loading" aria-live="polite">Chargement…</div>
        )}

        {toast && (
          <div className={`vrt-map__toast vrt-map__toast--${toast.kind}`} role="status">
            {toast.kind === 'info' && <Loader2 size={16} className="vrt-spin" />}
            <span>{toast.msg}</span>
          </div>
        )}
      </div>

      <Footer />

      <style>{`
        .vrt-map { min-height: 100vh; background: var(--vrt-bg); }
        @keyframes vrt-spin-kf { to { transform: rotate(360deg); } }
        .vrt-spin { animation: vrt-spin-kf 1s linear infinite; }
        .vrt-map__toast {
          position: absolute;
          left: 50%;
          bottom: 100px;
          transform: translateX(-50%);
          display: inline-flex;
          align-items: center;
          gap: 10px;
          max-width: calc(100% - 32px);
          padding: 12px 18px;
          border-radius: 999px;
          font-size: 14px;
          font-weight: 600;
          color: #fff;
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.28);
          z-index: 1100;
          animation: vrt-toast-in 0.15s ease-out;
        }
        .vrt-map__toast--info    { background: var(--vrt-navy); }
        .vrt-map__toast--success { background: var(--vrt-teal); }
        .vrt-map__toast--error   { background: var(--vrt-danger); }
        @keyframes vrt-toast-in {
          from { opacity: 0; transform: translate(-50%, 8px); }
          to   { opacity: 1; transform: translate(-50%, 0); }
        }
        .vrt-map__wrap {
          position: relative;
          width: 100%;
          height: calc(100dvh - 140px);
          min-height: 420px;
        }
        .vrt-map__wrap .leaflet-container { width: 100% !important; height: 100% !important; }
        .vrt-marker__dot {
          display: block;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: ${TEAL};
          border: 3px solid #fff;
          box-shadow: 0 1px 4px rgba(0,0,0,.35);
        }
        .vrt-marker__dot--priority {
          background: ${RED_PRIORITY};
        }
        .vrt-marker__dot--anomaly {
          background: ${VIOLET_ANOMALY};
        }
        .vrt-qmarker__bubble {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          border: 2.5px solid #fff;
          box-shadow: 0 2px 6px rgba(0,0,0,.4);
        }
        .vrt-map__filters {
          position: absolute;
          top: 16px;
          right: 16px;
          z-index: 1000;
          background: #fff;
          border: 1px solid var(--vrt-border);
          border-radius: 12px;
          padding: 10px 12px;
          box-shadow: 0 6px 20px rgba(0,0,0,.18);
          min-width: 200px;
          font-family: var(--vrt-font);
        }
        .vrt-map__filter-row {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 6px 4px;
          font-size: 14px;
          color: var(--vrt-navy);
          cursor: pointer;
          min-height: 36px;
        }
        .vrt-map__filter-row input[type="checkbox"] {
          width: 18px;
          height: 18px;
          accent-color: var(--vrt-teal);
          flex: 0 0 auto;
          cursor: pointer;
        }
        .vrt-map__filter-row:has(input:disabled) {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .vrt-map__filter-row--strong {
          font-weight: 700;
          color: ${RED_PRIORITY};
        }
        .vrt-map__filter-dot {
          display: inline-block;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: 2px solid #fff;
          box-shadow: 0 0 0 1px rgba(0,0,0,.1);
          flex: 0 0 auto;
        }
        .vrt-map__filter-count {
          color: #6b7c93;
          font-weight: 500;
        }
        .vrt-map__filter-sep {
          height: 1px;
          background: var(--vrt-border);
          margin: 6px 0 2px;
        }
        @media (max-width: 480px) {
          .vrt-map__filters { min-width: 0; padding: 8px 10px; }
          .vrt-map__filter-row { font-size: 13px; }
        }
        .vrt-map__floats {
          position: absolute;
          z-index: 1000;
          display: flex;
          gap: 8px;
        }
        .vrt-map__floats--tl { left: 16px; top: 16px; }
        .vrt-map__floats--br { right: 16px; bottom: 24px; }
        .vrt-map__floats--bl { left: 16px; bottom: 24px; }
        .vrt-map__toggle {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          min-height: 44px;
          padding: 0 16px;
          border-radius: 999px;
          background: var(--vrt-navy);
          color: #fff;
          font-weight: 600;
          font-size: 14px;
          box-shadow: 0 4px 14px rgba(0,0,0,.25);
          cursor: pointer;
        }
        .vrt-map__toggle input[type="checkbox"] {
          width: 18px;
          height: 18px;
          cursor: pointer;
          accent-color: var(--vrt-teal);
        }
        .vrt-map__legend {
          position: absolute;
          bottom: 24px;
          right: 16px;
          background: #fff;
          border: 1px solid var(--vrt-border);
          border-radius: 8px;
          padding: 12px 16px;
          box-shadow: 0 2px 8px rgba(0,0,0,.1);
          z-index: 999;
        }
        .vrt-map__legend-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: var(--vrt-navy);
          margin-bottom: 6px;
        }
        .vrt-map__legend-item:last-child {
          margin-bottom: 0;
        }
        .vrt-map__legend-dot {
          display: inline-block;
          width: 12px;
          height: 12px;
          border-radius: 50%;
        }
        .vrt-map__legend-dot--priority {
          background: ${RED_PRIORITY};
        }
        .vrt-map__legend-dot--standard {
          background: ${TEAL};
        }
        .vrt-map__locate {
          width: 56px; height: 56px;
          border-radius: 999px;
          background: var(--vrt-navy);
          color: #fff;
          border: none;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 14px rgba(0,0,0,.25);
        }
        .vrt-map__locate:hover { background: var(--vrt-navy-700); }
        .vrt-map__locate:disabled { opacity: 0.6; }
        .vrt-map__btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          min-height: 44px;
          padding: 0 16px;
          border-radius: 999px;
          font-weight: 600;
          font-size: 14px;
          text-decoration: none;
          box-shadow: 0 4px 14px rgba(0,0,0,.25);
        }
        .vrt-map__btn--navy { background: var(--vrt-navy); color: #fff; }
        .vrt-map__btn--navy:hover { background: var(--vrt-navy-700); color: #fff; }
        .vrt-map__loading {
          position: absolute; top: 16px; left: 50%; transform: translateX(-50%);
          background: rgba(26,40,73,.92); color: #fff;
          padding: 6px 14px; border-radius: 999px; font-size: 13px; z-index: 1001;
        }
        .vrt-map__error {
          position: absolute; top: 16px; left: 16px; right: 16px;
          background: #fdecea; color: #a23123;
          padding: 10px 14px; border-radius: 8px; z-index: 1001; font-size: 14px;
        }
        .leaflet-popup-content { margin: 10px 12px; font-family: var(--vrt-font); }
        .leaflet-popup-content-wrapper { border-radius: 12px; }
      `}</style>
    </div>
  );
}

function PopupContent({ a }) {
  const [imgErr, setImgErr] = useState(false);
  const navigate = useNavigate();
  const gmaps = `https://www.google.com/maps?q=${a.latitude},${a.longitude}`;
  const shortIdStr = a.id ? String(a.id).split('-')[0] : '';
  const titleLine = a.code ? `Avaloir ${a.code}` : (shortIdStr ? `Avaloir #${shortIdStr}` : 'Avaloir');

  const goDetail = () => {
    navigate(`/avaloir/${a.id}`);
  };

  return (
    <div style={{ minWidth: 200, maxWidth: 240 }}>
      {/* Photo */}
      {a.photo_url && !imgErr ? (
        <img
          src={a.photo_url}
          alt="Avaloir"
          onError={() => setImgErr(true)}
          style={{
            width: '100%',
            maxWidth: 250,
            maxHeight: 200,
            height: 'auto',
            objectFit: 'cover',
            borderRadius: 8,
            display: 'block',
            marginBottom: 10,
          }}
        />
      ) : (
        <div style={{
          width: '100%', height: 120, background: '#e9ecef',
          borderRadius: 8, marginBottom: 10,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#8a93a3', fontSize: 12,
        }}>
          {a.photo_url ? 'Photo indisponible' : 'Pas de photo'}
        </div>
      )}

      {/* Identifier + city */}
      <div style={{
        fontSize: 14,
        fontWeight: 700,
        color: 'var(--vrt-navy)',
        marginBottom: 4,
        lineHeight: 1.2,
      }}>
        {titleLine}
      </div>
      {a.city && (
        <div style={{
          fontSize: 12,
          color: 'var(--vrt-text-muted)',
          marginBottom: 8,
        }}>
          {a.city}
        </div>
      )}

      {/* Badges Priorité Life + Anomalie */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
        {a.priority_life && (
          <span style={{
            display: 'inline-block',
            background: RED_PRIORITY,
            color: '#fff',
            padding: '3px 9px',
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.04em',
          }}>
            PRIORITÉ LIFE
          </span>
        )}
        {a.anomaly && (
          <span style={{
            display: 'inline-block',
            background: VIOLET_ANOMALY,
            color: '#fff',
            padding: '3px 9px',
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.04em',
          }}>
            ANOMALIE
          </span>
        )}
      </div>

      {/* Detail button (full width, teal) */}
      <button
        type="button"
        onClick={goDetail}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          width: '100%',
          minHeight: 40,
          padding: '8px 14px',
          background: 'var(--vrt-teal)',
          color: '#fff',
          border: 'none',
          borderRadius: 8,
          fontSize: 14,
          fontWeight: 700,
          cursor: 'pointer',
          marginBottom: 8,
        }}
      >
        Voir le détail
        <ChevronRight size={16} />
      </button>

      {/* Discreet Google Maps link */}
      <div style={{ textAlign: 'center' }}>
        <a
          href={gmaps}
          target="_blank"
          rel="noreferrer"
          style={{
            fontSize: 11,
            color: 'var(--vrt-text-muted)',
            textDecoration: 'underline',
          }}
        >
          Google Maps
        </a>
      </div>
    </div>
  );
}

function QuickPointPopup({ p }) {
  const meta = {
    poubelle: { color: JAUNE_POUBELLE, label: 'Poubelle' },
    cendrier: { color: ORANGE_CENDRIER, label: 'Cendrier' },
    zone_sensible: { color: ROUGE_ZS, label: 'Zone sensible' },
  }[p.type] || { color: TEAL, label: p.type };
  const { color, label } = meta;
  const gmaps = `https://www.google.com/maps?q=${p.latitude},${p.longitude}`;
  const when = p.created_at ? new Date(p.created_at).toLocaleDateString('fr-FR', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }) : null;

  return (
    <div style={{ minWidth: 180, maxWidth: 260 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: color, color: '#fff',
          padding: '4px 10px', borderRadius: 999,
          fontSize: 12, fontWeight: 700,
        }}>
          {label}
        </span>
        {p.code && (
          <span style={{
            fontFamily: 'ui-monospace, monospace',
            fontWeight: 700,
            color: 'var(--vrt-navy)',
            fontSize: 12,
            letterSpacing: '0.05em',
          }}>{p.code}</span>
        )}
      </div>
      {p.photo_url && (
        <img
          src={p.photo_url}
          alt={label}
          style={{ width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 8, display: 'block', marginBottom: 8 }}
        />
      )}
      {p.city && (
        <div style={{ fontSize: 13, color: 'var(--vrt-navy)', fontWeight: 600 }}>
          {p.city}
        </div>
      )}
      {p.notes && (
        <div style={{ fontSize: 12, color: 'var(--vrt-navy)', marginTop: 4, fontStyle: 'italic' }}>
          « {p.notes} »
        </div>
      )}
      {when && (
        <div style={{ fontSize: 12, color: 'var(--vrt-text-muted)', marginTop: 2 }}>
          {when}
        </div>
      )}
      {Number.isFinite(p.accuracy_m) && (
        <div style={{ fontSize: 11, color: 'var(--vrt-text-muted)', marginTop: 4 }}>
          Précision : ±{Math.round(p.accuracy_m)} m
        </div>
      )}
      <div style={{ marginTop: 8 }}>
        <a
          href={gmaps}
          target="_blank"
          rel="noreferrer"
          style={{ fontSize: 11, color: 'var(--vrt-text-muted)', textDecoration: 'underline' }}
        >
          Google Maps
        </a>
      </div>
    </div>
  );
}
